import get from 'lodash/get';
import filter from 'lodash/filter';
import { mapBopsAvailability, formatDateYYYYMMDD, convertKebabCaseDateString } from 'utils';
import {
  productServiceWithInHouseData,
  cartService,
  cartServiceV2,
  productServiceWithInHouseDataV4,
  farfetchProductDetailsService,
  inventoryStatusService,
} from 'config/api';
import { mapProduct } from '../reducers/utils';

import actionTypes from './actionTypes';

const cartTypeMap = {
  concierge: 'CONCIERGE',
  personal: 'CUSTOMER',
};

const filterAndMapProducts = ({ productsData, cartServiceProducts, clientId, farfetchCheck = false }) => {
  return productsData
    .filter((product) => !product.error)
    .map((product) => {
      const currentProduct = cartServiceProducts[product.webProductSku]
        ? cartServiceProducts[product.webProductSku]
        : cartServiceProducts[product.itemskuId];
      if (farfetchCheck) {
        return {
          ...product,
          quantity: currentProduct?.quantity,
          customizations: currentProduct?.customizations,
          createdBy: currentProduct?.createdBy,
          clientId,
          arriveByDate:
            currentProduct?.arriveByDate || convertKebabCaseDateString(currentProduct?.perishableDeliveryByDate),
        };
      }
      const prodWithBops = mapBopsAvailability(product);

      return {
        ...prodWithBops,
        quantity: currentProduct?.quantity,
        customizations: currentProduct?.customizations,
        createdBy: currentProduct?.createdBy,
        clientId,
        arriveByDate:
          currentProduct?.arriveByDate || convertKebabCaseDateString(currentProduct?.perishableDeliveryByDate),
      };
    });
};

export const getCartProducts =
  ({ clientId, cartType, useApiV4, useCartServiceV2 = false, farfetchCheck = false }) =>
  async (dispatch) => {
    if (!farfetchCheck) {
      dispatch({
        type: actionTypes[`${cartTypeMap[cartType]}_CART_PRODUCTS_LOADING`],
      });
    }

    dispatch({
      type: actionTypes.REMOVE_ORDER_ID,
    });

    try {
      const versionnedCardServices = cartType === 'personal' && useCartServiceV2 ? cartServiceV2 : cartService;
      const cartServiceResponse = await versionnedCardServices.get(`/${clientId}@${cartType}`, { clientId });
      const cartServiceData = cartServiceResponse.data.items;

      const productsIds = Object.keys(cartServiceData).join(',');
      let products = [];
      let farfetchProducts = [];

      if (productsIds) {
        if (farfetchCheck) {
          dispatch({ type: actionTypes[`${cartTypeMap[cartType]}_FARFETCH_CART_PRODUCTS_LOADING`], payload: products });
          const farfetchProductServiceResponse = await farfetchProductDetailsService.get(
            `/pal/sku?skuIds=${productsIds}`,
          );
          const skuNotFound = filter(farfetchProductServiceResponse.data, 'error');

          if (skuNotFound) {
            dispatch({
              type: actionTypes[`${cartTypeMap[cartType]}_FARFETCH_CART_PRODUCTS_ERROR`],
              payload: skuNotFound,
            });
          }
          farfetchProducts = filterAndMapProducts({
            productsData: farfetchProductServiceResponse.data.filter((e) => e.itemskuId),
            cartServiceProducts: cartServiceData,
            clientId,
            farfetchCheck,
          });
          dispatch({
            type: actionTypes[`${cartTypeMap[cartType]}_FARFETCH_CART_PRODUCTS_SUCCESS`],
            payload: farfetchProducts,
          });
        } else {
          const productService = useApiV4 ? productServiceWithInHouseDataV4 : productServiceWithInHouseData;
          const productServiceResponse = await productService.get(`?product_id=${productsIds}`, {
            clientId,
          });
          products = filterAndMapProducts({
            productsData: productServiceResponse.data,
            cartServiceProducts: cartServiceData,
            clientId,
            farfetchCheck,
          });
        }
      }
      if (!farfetchCheck) {
        dispatch({ type: actionTypes[`${cartTypeMap[cartType]}_CART_PRODUCTS_SUCCESS`], payload: products });
      }
    } catch (error) {
      dispatch({ type: actionTypes[`${cartTypeMap[cartType]}_CART_PRODUCTS_ERROR`], payload: error });
    }
  };

export const resetSaveForLaterFarfetchProducts = () => async (dispatch) => {
  dispatch({
    type: actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_RESET,
  });
};
export const setFarfetchProduct = (productsData, cartType) => ({
  type: actionTypes[`${cartTypeMap[cartType]}_FARFETCH_CART_PRODUCTS_SUCCESS`],
  payload: productsData,
});

export const setSaveForLaterFarfetchProduct = (productsData) => ({
  type: actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_SUCCESS,
  payload: productsData,
});

const getProductInfo = async ({ SKU, cartType, clientId, qty, farfetchCheck }) => {
  let errorMessage = 'Service unavailable - try again later';
  if (!SKU) {
    errorMessage = 'Invalid SKU';
    throw errorMessage;
  }

  const isPersonalCartType = cartType === 'personal';

  let query = `?product_id=${SKU}`;
  if (isPersonalCartType) {
    query += '&with_web_product_ids=true';
  }

  let personalization;
  let inventory;
  let backorderFlag;
  let itemskuId;
  let product;
  let farfetchProductService = null;
  if (farfetchCheck) {
    farfetchProductService = await farfetchProductDetailsService.get(`/pal/sku?skuIds=${SKU}`, {
      httpClientOptions: { handleErrors: false },
      clientId,
    });
    const farfetchInventoryService = await inventoryStatusService.post('/retail/inventory', { pimSkuIds: [SKU] });
    product = get(farfetchProductService, 'data.0');
    const farfetchInventory = get(farfetchInventoryService, 'data.0');
    ({ backorderFlag, itemskuId, personalization } = product);
    inventory = farfetchInventory.quantity;
    product.inventory = inventory;
  } else {
    const productService = productServiceWithInHouseDataV4;

    const productResponse = await productService.get(query, {
      httpClientOptions: { handleErrors: false },
      clientId,
    });
    product = get(productResponse, 'data.0');
    personalization = product.personalization;
    ({ personalization, backorderFlag, itemskuId, inventory } = product);
  }

  const outOfStock = (inventory === 0 || qty > inventory) && backorderFlag !== 'Y';
  const isRestrictedItem = personalization === 'R';
  const hasError = product.error || outOfStock || !itemskuId || isRestrictedItem;

  if (product.error || !product.itemskuId) {
    errorMessage = product?.displayMessage || 'Invalid SKU';
  } else if (outOfStock) {
    errorMessage = `${SKU} is out of stock`;
  } else if (isRestrictedItem) {
    errorMessage = 'Restricted item - Cannot add to the cart';
  }

  if (hasError) {
    throw errorMessage;
  }

  return [
    {
      ...product,
      quantity: 1, // By default add only 1
      clientId,
    },
    isPersonalCartType ? product.webProductSku : product.itemskuId,
  ];
};

export const getProductAndAddToCart =
  ({ searchId, cartType, clientId, productQty, farfetchCheck = false, deliveryDate }) =>
  async (dispatch, getState) => {
    dispatch({ type: actionTypes.GET_PRODUCT_BY_SKU_LOADING });

    try {
      const [product, itemId] = await getProductInfo({
        SKU: searchId,
        cartType,
        clientId,
        qty: productQty,
        farfetchCheck,
      });
      const { isRestricted, isParenthetical, isPerishable } = product;

      if (isRestricted && (isParenthetical || isPerishable)) {
        const errorMessage = 'Product Is Restricted';
        dispatch({ type: actionTypes.GET_PRODUCT_BY_SKU_ERROR, payload: errorMessage });

        throw errorMessage;
      }
      if (product?.price?.currentPrice === '0.00') {
        const errorMessage = 'Cannot add $0 item to the bag.';
        dispatch({
          type: actionTypes.GET_PRODUCT_BY_SKU_ERROR,
          payload: errorMessage,
        });

        throw errorMessage;
      }

      dispatch({ type: actionTypes.GET_PRODUCT_BY_SKU_SUCCESS, payload: { ...product, searchId } });

      try {
        const { user = {}, clientsModule = {} } = getState();
        const {
          conciergeProducts = [],
          customerProducts = [],
          farfetchConciergeProducts = [],
        } = clientsModule.cart || {};
        let products = cartType === 'concierge' ? conciergeProducts : customerProducts;
        if (farfetchCheck) {
          products = farfetchConciergeProducts;
        }
        const existingProduct = products.find((p) => p.itemskuId === itemId);
        const { employeeNumber, role } = user.data || {};
        const createdBy = existingProduct ? existingProduct.createdBy : `${role}/${employeeNumber}`;

        await cartService.post(
          `/${clientId}@${cartType}`,
          [
            {
              itemId,
              quantity: product.quantity,
              createdBy,
              ...(deliveryDate && { arriveByDate: formatDateYYYYMMDD(deliveryDate) }),
            },
          ],
          { clientId },
        );

        const actionsMap = {
          concierge: farfetchCheck
            ? actionTypes.ADD_PRODUCT_TO_FARFETCH_CONCIERGE_CART_SUCCESS
            : actionTypes.ADD_PRODUCT_TO_CONCIERGE_CART_SUCCESS,
          personal: actionTypes.ADD_PRODUCT_TO_CUSTOMER_CART_SUCCESS,
        };
        dispatch({ type: actionsMap[cartType], payload: { ...product, createdBy } });
      } catch (error) {
        dispatch({ type: actionTypes.ADD_PRODUCT_TO_CART_ERROR, payload: error });
        return 'error';
      }
    } catch (err) {
      const error = err?.response ? 'Service unavailable - try again later' : err;
      dispatch({ type: actionTypes.GET_PRODUCT_BY_SKU_ERROR, payload: error });
      dispatch({
        type: '@SHELL_ADD_NOTIFICATION',
        payload: {
          id: 'unhandledAddToBagError',
          type: 'error',
          message: error,
        },
      });
      return 'error';
    }
    return 'success';
  };

export const changeSKU = (SKU) => ({
  type: actionTypes.CHANGE_SKU,
  payload: SKU,
});

export const removeItemFromCart =
  ({ clientId, product, cartType, isBgFarfetch }) =>
  async (dispatch) => {
    const cartTypeMap = {
      concierge: 'CONCIERGE',
      personal: 'CUSTOMER',
    };

    dispatch({
      type: actionTypes[`REMOVE_ITEM_FROM_${cartTypeMap[cartType]}_CART_LOADING`],
    });

    try {
      const itemId = cartType === 'personal' ? product.webProductSku : product.itemskuId;
      await cartService.post(
        `/${clientId}@${cartType}`,
        [
          {
            itemId,
            quantity: -product.quantity,
            cretaedBy: product.createdBy,
          },
        ],
        { clientId },
      );

      dispatch({
        type: isBgFarfetch
          ? actionTypes[`REMOVE_FARFETCH_ITEM_FROM_${cartTypeMap[cartType]}_CART_SUCCESS`]
          : actionTypes[`REMOVE_ITEM_FROM_${cartTypeMap[cartType]}_CART_SUCCESS`],
        payload: itemId,
      });
    } catch (err) {
      dispatch({
        type: actionTypes[`REMOVE_ITEM_FROM_${cartTypeMap[cartType]}_CART_ERROR`],
      });
    }
  };

export const changeCartItemQuantity =
  ({ clientId, mathOperator, changedItem, cartType, farfetchCheck = false }) =>
  async (dispatch) => {
    const cartTypeMap = {
      concierge: 'CONCIERGE',
      personal: 'CUSTOMER',
    };

    dispatch({
      type: actionTypes[`CHANGE_${cartTypeMap[cartType]}_CART_ITEM_LOADING`],
      payload: changedItem,
    });

    try {
      const itemId = cartType === 'personal' ? changedItem.webProductSku : changedItem.itemskuId;
      await cartService.post(
        `/${clientId}@${cartType}`,
        [
          {
            itemId,
            quantity: mathOperator === '-' ? -1 : 1,
            createdBy: changedItem.createdBy,
            ...(changedItem.arriveByDate && { arriveByDate: changedItem.arriveByDate }),
          },
        ],
        { clientId },
      );

      if (farfetchCheck) {
        dispatch({
          type: actionTypes[`CHANGE_FARFETCH_${cartTypeMap[cartType]}_CART_ITEM_SUCCESS`],
          payload: changedItem,
        });
      }

      dispatch({
        type: actionTypes[`CHANGE_${cartTypeMap[cartType]}_CART_ITEM_SUCCESS`],
        payload: changedItem,
      });
    } catch (err) {
      dispatch({
        type: actionTypes[`CHANGE_${cartTypeMap[cartType]}_CART_ITEM_ERROR`],
        payload: changedItem,
      });
    }
  };

export const changeCartItemDeliveryDate = (clientId, date, changedItem) => async (dispatch) => {
  dispatch({
    type: actionTypes.CHANGE_CONCIERGE_CART_ITEM_LOADING,
    payload: changedItem,
  });

  const newDeliveryDate = formatDateYYYYMMDD(date);

  try {
    const { itemskuId } = changedItem;
    await cartService.post(
      `/${clientId}@concierge`,
      [
        {
          itemId: itemskuId,
          createdBy: changedItem.createdBy,
          arriveByDate: formatDateYYYYMMDD(date),
        },
      ],
      { clientId },
    );
    dispatch({
      type: actionTypes.CHANGE_CONCIERGE_CART_ITEM_SUCCESS,
      payload: {
        ...changedItem,
        arriveByDate: newDeliveryDate,
      },
    });
  } catch (error) {
    dispatch({
      type: actionTypes.CHANGE_CONCIERGE_CART_ITEM_ERROR,
      payload: changedItem,
    });
  }
};

export const adjustProductPrice =
  (clientId, itemskuId, adjustedPriceData = {}) =>
  async (dispatch, getState) => {
    dispatch({
      type: actionTypes.PRODUCT_PRICE_ADJUSTMENT_LOADING,
    });

    try {
      const { clientsModule } = getState();
      const { conciergeProducts = [] } = clientsModule?.cart || {};
      const product = conciergeProducts.find((p) => p.itemskuId === itemskuId) || {};

      await cartService.post(
        `/${clientId}@concierge`,
        [
          {
            itemId: itemskuId,
            customizations: adjustedPriceData,
            createdBy: product.createdBy,
          },
        ],
        { clientId },
      );

      dispatch({
        type: actionTypes.PRODUCT_PRICE_ADJUSTMENT_SUCCESS,
        payload: { itemskuId, ...adjustedPriceData },
      });
    } catch (err) {
      dispatch({
        type: actionTypes.PRODUCT_PRICE_ADJUSTMENT_ERROR,
      });
    }
  };

export const addGiftOptions =
  ({ clientId, itemskuId, giftOptions }) =>
  async (dispatch, getState) => {
    const trimmedGiftOptions = Object.keys(giftOptions).reduce((acc, key) => {
      if (key === 'giftReceipt') {
        acc[key] = giftOptions[key];
      } else {
        acc[key] = giftOptions[key].trim();
      }

      return acc;
    }, {});

    dispatch({
      type: actionTypes.GIFT_OPTIONS_LOADING,
    });

    try {
      const { clientsModule } = getState();
      const { conciergeProducts = [] } = clientsModule?.cart || {};
      const product = conciergeProducts.find((p) => p.itemskuId === itemskuId) || {};

      await cartService.post(
        `/${clientId}@concierge`,
        [
          {
            itemId: itemskuId,
            customizations: trimmedGiftOptions,
            createdBy: product.createdBy,
          },
        ],
        { clientId },
      );

      dispatch({
        type: actionTypes.GIFT_OPTIONS_SUCCESS,
        payload: { clientId, itemskuId, giftOptions: trimmedGiftOptions },
      });
    } catch (err) {
      dispatch({
        type: actionTypes.GIFT_OPTIONS_ERROR,
      });
    }
  };

/** SFL actions */
export const getSaveForLaterProducts =
  (clientId, useApiV4, farfetchCheck = false) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.SAVE_FOR_LATER_PRODUCTS_LOADING,
    });

    const productService = useApiV4 ? productServiceWithInHouseDataV4 : productServiceWithInHouseData;

    try {
      const response = await cartService.get(`/${clientId}@conciergeSaveForLater`, { clientId });
      const saveForLaterData = response.data.items;
      const productsIds = Object.keys(saveForLaterData).join(',');
      let products = [];
      let farfetchProducts = [];

      if (productsIds) {
        const productServiceResponse = await productService.get(`?product_id=${productsIds}`, {
          clientId,
        });
        products = filterAndMapProducts({
          productsData: productServiceResponse.data,
          cartServiceProducts: saveForLaterData,
          clientId,
          farfetchCheck,
        });
        if (farfetchCheck) {
          const farfetchProductServiceResponse = await farfetchProductDetailsService.get(
            `/pal/sku?skuIds=${productsIds}`,
          );

          if (farfetchProductServiceResponse.data?.error) {
            dispatch({
              type: actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_ERROR,
              payload: farfetchProductServiceResponse.data,
            });
          } else {
            farfetchProducts = filterAndMapProducts({
              productsData: farfetchProductServiceResponse.data,
              cartServiceProducts: saveForLaterData,
              clientId,
              farfetchCheck,
            });
            dispatch({ type: actionTypes.SAVE_FOR_LATER_FARFETCH_PRODUCTS_SUCCESS, payload: farfetchProducts });
          }
        }
      }

      dispatch({ type: actionTypes.SAVE_FOR_LATER_PRODUCTS_SUCCESS, payload: products });
    } catch (error) {
      dispatch({ type: actionTypes.SAVE_FOR_LATER_PRODUCTS_ERROR, payload: error });
    }
  };

export const moveProductToSaveForLater =
  (clientId, product, isBgFarfetch = false) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_LOADING });

    try {
      await cartService.post(
        `/${clientId}@conciergeSaveForLater`,
        [
          {
            itemId: product.itemskuId,
            moveFromCart: 'concierge',
            createdBy: product.createdBy,
          },
        ],
        { clientId },
      );

      const successPayload = {
        ...product,
        price: {
          ...product.price,
          adjustedPrice: '',
        },
      };
      if (successPayload.isGiftWrappable) {
        const { giftOptions } = mapProduct({});
        successPayload.giftOptions = giftOptions;
      }

      dispatch({
        type: isBgFarfetch
          ? actionTypes.ADD_FARFETCH_ITEM_TO_SAVE_FOR_LATER_SUCCESS
          : actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_SUCCESS,
        payload: successPayload,
      });
    } catch (error) {
      dispatch({ type: actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_ERROR, payload: error });
    }
  };

export const moveBatchProductsToSaveForLater =
  (clientId, products = []) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_LOADING });

    const batchProducts = products.map((product) => ({
      itemId: product.itemskuId,
      moveFromCart: 'concierge',
      createdBy: product.createdBy,
    }));

    try {
      await cartService.post(`/${clientId}@conciergeSaveForLater`, batchProducts, { clientId });

      const successPayload = products.reduce((acc, curr) => {
        const product = {
          ...curr,
          price: {
            ...curr.price,
            adjustedPrice: '',
          },
        };

        if (product.isGiftWrappable) {
          const { giftOptions } = mapProduct({});
          product.giftOptions = giftOptions;
        }

        acc.push(product);
        return acc;
      }, []);

      dispatch({ type: actionTypes.MOVE_BATCH_ITEMS_TO_SAVE_FOR_LATER_SUCCESS, payload: successPayload });
    } catch (error) {
      dispatch({ type: actionTypes.ADD_ITEM_TO_SAVE_FOR_LATER_ERROR, payload: error });
    }
  };

export const removeProductFromSaveForLater =
  (clientId, product, isBgFarfetch = false) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.REMOVE_ITEM_FROM_SAVE_FOR_LATER_LOADING });

    try {
      await cartService.post(
        `/${clientId}@conciergeSaveForLater`,
        [
          {
            itemId: product.itemskuId,
            quantity: -product.quantity,
            createdBy: product.createdBy,
          },
        ],
        { clientId },
      );
      dispatch({
        type: isBgFarfetch
          ? actionTypes.REMOVE_FARFETCH_ITEM_FROM_SAVE_FOR_LATER_SUCCESS
          : actionTypes.REMOVE_ITEM_FROM_SAVE_FOR_LATER_SUCCESS,
        payload: product,
      });
    } catch (error) {
      dispatch({ type: actionTypes.REMOVE_ITEM_FROM_SAVE_FOR_LATER_ERROR, payload: error });
    }
  };

export const _getQuantityToMoveFromSFL = (sflProduct = {}, conciergeProduct) => {
  const { quantity: sflQty, inventory, isBackorder, isPerishable } = sflProduct;
  const isConciergeProductExists = !!conciergeProduct;
  const isOutOfStock = sflQty > inventory && !isBackorder && !isPerishable;

  let quantity = sflQty || 0;

  // do not check inventory for perishable products
  if (isPerishable) {
    return quantity;
  }

  if (isConciergeProductExists) {
    const conQty = conciergeProduct.quantity;
    if (isOutOfStock) {
      quantity = inventory - conQty;
    } else if (conQty + sflQty > inventory) {
      if (!isBackorder) {
        quantity = inventory - conQty;
      } else {
        quantity = sflQty;
      }
    }
  } else if (isOutOfStock) {
    quantity = inventory;
  }

  return quantity < 0 ? 0 : quantity;
};

export const moveProductFromSflToConcierge =
  (clientId, product = {}, isBgFarfetch = false) =>
  async (dispatch, getState) => {
    dispatch({ type: actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_LOADING });

    try {
      const body = {
        itemId: product.itemskuId,
        moveFromCart: 'conciergeSaveForLater',
        createdBy: product.createdBy,
      };

      const { clientsModule } = getState();
      const { conciergeProducts = [] } = clientsModule?.cart;
      const existingProduct = conciergeProducts.find((p) => p.itemskuId === product.itemskuId);
      const quantity = _getQuantityToMoveFromSFL(product, existingProduct);

      if (quantity === 0) {
        // Remove product if nothing to move
        removeProductFromSaveForLater(clientId, product, isBgFarfetch)(dispatch);
        return;
      }

      if (quantity !== product.quantity) {
        // add quantity to request body
        body.quantity = quantity;
      }

      await cartService.post(`/${clientId}@concierge`, [body], { clientId });

      const payload = {
        ...product,
        quantity: body.quantity || product.quantity,
      };

      dispatch({
        type: isBgFarfetch
          ? actionTypes.MOVE_FARFETCH_ITEM_FROM_SFL_TO_CONCIERGE_SUCCESS
          : actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_SUCCESS,
        payload,
      });
    } catch (error) {
      dispatch({ type: actionTypes.MOVE_ITEM_FROM_SFL_TO_CONCIERGE_ERROR, payload: error });
    }
  };

export const moveOnlineBagProductsToConcierge = (clientId, products) => async (dispatch) => {
  dispatch({ type: actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_LOADING });

  try {
    const body = products.map(({ itemskuId, quantity, createdBy, arriveByDate }) => ({
      quantity,
      createdBy,
      itemId: itemskuId,
      ...(arriveByDate && { arriveByDate: formatDateYYYYMMDD(arriveByDate) }),
    }));

    await cartService.post(`/${clientId}@concierge`, body);

    dispatch({ type: actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_SUCCESS, payload: body });
  } catch (error) {
    dispatch({ type: actionTypes.MOVE_ONLINE_BAG_PRODUCTS_TO_CONCIERGE_ERROR, payload: error });
  }
};
